import React, { forwardRef, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import _get from 'lodash/get';
import Button, {
  ButtonProps,
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import FileUploaderUppy, {
  AttachmentValue,
  UploaderView,
} from '@lib/components/FileUploaderUppy/FileUploaderUppy';
import Placeholder from '@lib/assets/images/preview-placeholder.svg';
import Image from '@lib/components/Image/Image';
import { materialSymbolsOutlined } from '@lib/enums/common';
import useIsOnline from '@lib/hooks/useIsOnline';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageProps,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import { FilesPreview } from '@lib/components/FilesUploader/enums';
import styles from './FilesUploader.module.scss';

export type FilesUploaderProps = {
  allowedFileTypes: string[];
  bodyClassName?: string;
  className?: string;
  disabled?: boolean;
  filesView?: FilesPreview;
  maxFileSize?: number;
  note?: string;
  onChange: (variables: AttachmentValue) => void;
  openUploaderModalOnInit?: boolean;
  showDeleteAllButton?: boolean;
  showOfflineMessage?: boolean;
  supportiveMessageProps?: SupportiveMessageProps;
  uploadButtonProps?: ButtonProps;
  useReuploadButton?: boolean;
  useUploadStrategy?: boolean;
  value?: AttachmentValue;
};

function ImageView({ value }: { value: AttachmentValue | undefined }) {
  if (!value) return null;
  const url = value?.urls?.large || value?.urls?.original || value?.url;
  if (_get(value, '_destroy')) return null;
  return (
    <div className={styles.images}>
      <div className={styles.img} key={uuidv4()}>
        {(url || Placeholder) && <Image src={url || Placeholder} />}
      </div>
    </div>
  );
}

function DocumentView({
  value,
  onReplaceAttachment,
  onDeleteAttachment,
  useReuploadButton,
  disabled,
}: {
  value: AttachmentValue | undefined;
  onReplaceAttachment: () => void;
  onDeleteAttachment: (id?: string, itemValueFileId?: string) => void;
  useReuploadButton?: boolean;
  disabled?: boolean;
}) {
  if (!value) return null;
  const itemValueId = _get(value, 'id');
  const itemValueFileId = _get(value, 'file.id');
  const actionButton =
    (itemValueId || itemValueFileId) && !disabled ? (
      <IconButton
        icon={useReuploadButton ? 'upload' : 'close'}
        symbolsOutlined
        type={IconButtonTypes.PrimaryOutlined}
        onClick={() =>
          useReuploadButton
            ? onReplaceAttachment()
            : onDeleteAttachment(itemValueId, itemValueFileId)
        }
      />
    ) : null;
  return (
    <div>
      <DocumentationPreview
        key={uuidv4()}
        attachment={value}
        useDefaultPdfPreview={!value.id}
        view={DocumentationPreviewEnum.DocumentRowCard}
        actions={actionButton}
      />
    </div>
  );
}

const FileUploader = forwardRef(
  (props: FilesUploaderProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    const { t } = useTranslation();
    const btnRef = useRef<HTMLButtonElement>(null);
    const isOnline = useIsOnline();
    const {
      allowedFileTypes,
      bodyClassName,
      className,
      disabled,
      filesView = FilesPreview.Images,
      maxFileSize,
      note,
      onChange,
      openUploaderModalOnInit,
      showDeleteAllButton,
      showOfflineMessage,
      supportiveMessageProps,
      uploadButtonProps,
      useReuploadButton,
      useUploadStrategy,
      value,
    } = props;

    const {
      leftIcon = 'edit',
      buttonSize = ButtonSizes.small,
      buttonType = ButtonTypes.primaryOutlined,
      buttonText = t('upload-file'),
      fullWidth = false,
      ...restUploadButtonProps
    } = uploadButtonProps || {};

    // @ts-ignore Field _destroy is a custom field
    const preparedValue = value && !_get(value, '_destroy') ? [value] : [];
    const displayUploadButton = !preparedValue?.length;
    const uploadButton = ({ onClick }: { onClick: () => void }) => (
      <div style={{ display: preparedValue?.length ? 'none' : 'block' }}>
        <Button
          ref={btnRef}
          onClick={onClick}
          buttonText={buttonText}
          buttonType={buttonType}
          leftIcon={leftIcon}
          leftIconClassName={materialSymbolsOutlined}
          buttonSize={buttonSize}
          disabled={disabled || !isOnline}
          fullWidth={fullWidth}
          {...restUploadButtonProps}
        />
      </div>
    );

    const onDeleteAttachment = (id?: string, itemValueFileId?: string) => {
      if (!value) return;
      if (value?.id === id || _get(value, 'file.id') === itemValueFileId) {
        onChange({ ...value, _destroy: true });
      }
    };

    const onReplaceAttachment = () => {
      btnRef.current?.click();
    };

    const onDeleteAllAttachments = () => {
      if (!value?.id) return;
      onChange({ id: value.id, _destroy: true } as AttachmentValue);
    };

    return (
      <div className={className}>
        {filesView === FilesPreview.Images && preparedValue?.length > 0 && (
          <ImageView value={preparedValue[0]} />
        )}
        {filesView === FilesPreview.Documents && preparedValue?.length > 0 && (
          <DocumentView
            value={preparedValue[0]}
            onReplaceAttachment={onReplaceAttachment}
            onDeleteAttachment={onDeleteAttachment}
            useReuploadButton={useReuploadButton}
            disabled={disabled}
          />
        )}
        {displayUploadButton ? (
          <div
            className={classNames(
              styles.body,
              { [styles.bodyWithFullWidthBtn]: fullWidth },
              bodyClassName,
            )}
          >
            <FileUploaderUppy
              allowedFileTypes={allowedFileTypes}
              maxFileSize={maxFileSize}
              maxNumberOfFiles={1}
              note={note}
              onChange={onChange}
              openButton={uploadButton}
              openUploaderModalOnInit={openUploaderModalOnInit}
              ref={ref}
              supportiveMessageProps={supportiveMessageProps}
              type={UploaderView.modal}
              useUploadStrategy={useUploadStrategy}
              value={value}
            />
            {showDeleteAllButton && preparedValue.length > 0 && (
              <Button
                buttonText={t('delete-all')}
                buttonType={ButtonTypes.negativeOutlined}
                leftIcon="delete"
                leftIconClassName={materialSymbolsOutlined}
                buttonSize={ButtonSizes.small}
                onClick={() => onDeleteAllAttachments()}
                disabled={disabled}
              />
            )}
          </div>
        ) : null}

        {!isOnline && showOfflineMessage && (
          <div style={{ marginTop: '16px' }}>
            <SupportiveMessage
              color={SupportiveMessageColor.Yellow}
              text={t('attachment-field-offline-short-text')}
              fullWidth
            />
          </div>
        )}
      </div>
    );
  },
);

export default memo(FileUploader);
